import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cargomatic/ui';

import { validateLoadDetailsV2 } from 'helpers/booking/validation';

import { isValid } from 'components/modules/booking/BookingHelper';
import LoadDetailsForm from 'components/load/LoadDetailsForm';
import { DEFAULT_WEIGHT_UNIT } from 'utils/localization/types';

function renderErrors(errors) {
  if (Object.keys(errors).length === 0) {
    return null;
  }
  return (
    <ul className="mt-2 mb-0 pl-5 list-disc text-sm text-red-700">
      {Object.keys(errors).map((key) => (
        <li key={key}>{errors[key]}</li>
      ))}
    </ul>
  );
}

const getInitialState = (props) => {
  return {
    totalWeight: props.shipment ? props.shipment.weight || 0 : 0,
    weight_units: props.shipment
      ? props.shipment.weight_units || DEFAULT_WEIGHT_UNIT
      : DEFAULT_WEIGHT_UNIT,
    stop: props.stop || {},
    loads: props.stop ? props.stop.loads || [] : [],
    full_truckload: props.stop ? props.stop.full_truckload || false : false,
    errors: {},
  };
};

class EditLoadModal extends Component {
  constructor(props) {
    super(props);
    this.state = getInitialState(props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.stop, nextProps.stop)) {
      this.setState({
        stop: nextProps.stop,
        loads: nextProps.stop.loads,
        full_truckload: nextProps.stop.full_truckload,
      });
    }
    if (this.props.shipment.weight !== nextProps.shipment.weight) {
      this.setState({ totalWeight: nextProps.shipment.weight });
    }
  }

  close() {
    this.props.onHide();
    this.setState(getInitialState(this.props));
  }

  isMultiStop = () => {
    return this.props.shipment.stops.length > 2;
  };

  validateLoad() {
    let valid = true;
    const {
      loads,
      full_truckload,
      totalWeight: weight,
      weight_units,
    } = this.state;
    const { t } = this.props;
    const loadDetails = validateLoadDetailsV2({
      loads,
      full_truckload,
      weight,
      weight_units,
      t,
    });
    if (!isValid(loadDetails)) {
      valid = false;
      this.setState({ errors: { loadDetails } });
    } else {
      this.setState({ errors: {} });
    }
    return valid;
  }

  addLoad = (newLoads) => {
    this.setState({ loads: newLoads });
  };

  updateTotalWeight = () => {
    this.setState((prevState) => {
      return {
        totalWeight: prevState.loads.reduce((total, load) => {
          return total + load.weight;
        }, 0),
      };
    });
  };

  handleRemoveLoad = (newLoads) =>
    this.setState({ loads: newLoads }, this.updateTotalWeight);

  handleLoadFieldChange = (newLoads, field) => {
    this.setState({ loads: newLoads }, () => {
      if (field === 'weight') {
        const totalWeight = newLoads.reduce((total, load) => {
          return total + load.weight;
        }, 0);
        this.setState({ totalWeight });
      }
    });
  };

  handleLoadSave = () => {
    if (this.validateLoad()) {
      const { totalWeight, loads, full_truckload } = this.state;
      this.props.onUpdateLoad(
        this.props.stop,
        totalWeight,
        loads,
        full_truckload,
        false,
      );
      this.setState(getInitialState(this.props));
    }
  };

  handleStopLoadSave = () => {
    if (this.validateLoad()) {
      const { totalWeight, loads, full_truckload } = this.state;
      this.props.onUpdateLoad(
        this.props.stop,
        totalWeight,
        loads,
        full_truckload,
        true,
      );
      this.setState(getInitialState(this.props));
    }
  };

  onTotalShipmentWeightChange = (value) => {
    this.setState({ totalWeight: value });
  };

  handleOpenChange = (open) => {
    if (!open) {
      this.close();
    }
  };

  renderLoadDetailErrors() {
    const {
      errors: { loadDetails },
    } = this.state;
    if (!loadDetails) {
      return null;
    }
    const blocks = [];
    const nonLoadItemErrors = _.omit(loadDetails, 'loadItemErrors');
    if (!_.isEmpty(nonLoadItemErrors)) {
      blocks.push(renderErrors(nonLoadItemErrors));
    }
    return (
      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
        {blocks}
        {(loadDetails.loadItemErrors || []).map((itemError, i) => (
          <React.Fragment key={i}>{renderErrors(itemError)}</React.Fragment>
        ))}
      </div>
    );
  }

  render() {
    if (_.isEmpty(this.props.stop)) {
      return null;
    }
    const { t } = this.props;
    return (
      <Dialog open={this.props.show} onOpenChange={this.handleOpenChange}>
        <DialogContent className="w-[95vw] max-w-[1400px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('LOAD_DETAILS')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {this.renderLoadDetailErrors()}
            <LoadDetailsForm
              loads={this.state.loads}
              fullTruckload={this.state.full_truckload}
              totalShipmentWeight={this.state.totalWeight}
              shipmentWeightUnit={this.state.weight_units}
              addLoad={this.addLoad}
              onRemoveLoad={this.handleRemoveLoad}
              onLoadFieldChange={this.handleLoadFieldChange}
              onTotalShipmentWeightChange={this.onTotalShipmentWeightChange}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={this.close.bind(this)}>
              {t('CLOSE')}
            </Button>
            {this.isMultiStop() && (
              <Button variant="secondary" onClick={this.handleStopLoadSave}>
                {t('SAVE_TO_THIS_STOP')}
              </Button>
            )}
            <Button onClick={this.handleLoadSave}>{titleize(t('SAVE'))}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

EditLoadModal.propTypes = {
  shipment: PropTypes.object.isRequired,
  stop: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
  onUpdateLoad: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};

export default withTranslation()(EditLoadModal);
